/**
 * Every control that in-app help may point at. A control opts in by spreading
 * `helpAnchor(id)` onto its element, which sets `data-help-anchor`. Help
 * content names anchors by these ids, so renaming one here fails the type
 * check at both ends instead of leaving a tour pointing at nothing.
 *
 * Never point help at test ids or class names: they change for other reasons.
 */
export const HELP_ANCHORS = ["eventdevice.live-pause"] as const;

export type HelpAnchor = (typeof HELP_ANCHORS)[number];

export const helpAnchor = (id: HelpAnchor) => ({ "data-help-anchor": id });

/** The anchored element if it is on the page now. */
export const findHelpAnchor = (id: HelpAnchor): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-help-anchor="${id}"]`);

/**
 * Resolves with the anchored element once it is on the page, or null after
 * `timeoutMs`. Covers a control that renders a moment after a route change
 * or after a tour step's `before` opened its container.
 */
export const waitForHelpAnchor = (
  id: HelpAnchor,
  timeoutMs = 1500
): Promise<HTMLElement | null> =>
  new Promise((resolve) => {
    const started = Date.now();
    const poll = () => {
      const element = findHelpAnchor(id);
      if (element || Date.now() - started >= timeoutMs) {
        resolve(element);
        return;
      }
      window.setTimeout(poll, 100);
    };
    poll();
  });
